import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth, useMyRole } from './lib/auth';
import { Login } from './pages/Login';
import { WorkOrders } from './pages/WorkOrders';
import { AssetPage } from './pages/AssetPage';
import { AssetsList } from './pages/AssetsList';
import { CsvImport } from './pages/CsvImport';
import { QrLabels } from './pages/QrLabels';
import { PropertiesList } from './pages/PropertiesList';
import { PropertyMap } from './pages/PropertyMap';
import { ClientsList } from './pages/ClientsList';
import { Parts } from './pages/Parts';
import { Tools } from './pages/Tools';
import { CommercialSettings } from './pages/CommercialSettings';
import { DueDates } from './pages/DueDates';
import { ChecklistTemplates } from './pages/ChecklistTemplates';
import { Preventive } from './pages/Preventive';
import { Overview } from './pages/Overview';
import { Reports } from './pages/Reports';
import { Leads } from './pages/Leads';
import { Referrers } from './pages/Referrers';
import { Marketing } from './pages/Marketing';
import { Catalogue } from './pages/Catalogue';
import { Quotes } from './pages/Quotes';
import { QuoteAccept } from './pages/QuoteAccept';
import { QuotePrint } from './pages/QuotePrint';
import { Contracts } from './pages/Contracts';
import { Invoices } from './pages/Invoices';
import { InvoicePrint } from './pages/InvoicePrint';
import { SalesDashboard } from './pages/SalesDashboard';
import { Accounting } from './pages/Accounting';
import { Suppliers } from './pages/Suppliers';
import { ManagementCompanies } from './pages/ManagementCompanies';
import { Directory } from './pages/Directory';
import { Payables } from './pages/Payables';
import { Treasury } from './pages/Treasury';
import { Pnl } from './pages/Pnl';
import { Statements } from './pages/Statements';
import { StatementPrint } from './pages/StatementPrint';
import { Profitability } from './pages/Profitability';
import { Exports } from './pages/Exports';
import { FinanceDashboard } from './pages/FinanceDashboard';
import { Users } from './pages/Users';
import { Settings } from './pages/Settings';
import { OwnerPortal } from './pages/OwnerPortal';
import { Help } from './pages/Help';
import { Styleguide } from './pages/Styleguide';
import { SetPassword } from './pages/SetPassword';

export function App() {
  const { session, loading, recovery } = useAuth();
  const { role, ready } = useMyRole();

  // An invite / password-recovery link takes precedence over everything: the
  // user must set a password before entering the app.
  if (recovery) return <SetPassword />;

  if (loading || (session && !ready)) {
    return <div className="grid min-h-screen place-items-center bg-bg text-muted">…</div>;
  }
  if (!session) {
    return (
      <Routes>
        <Route path="/styleguide" element={<Styleguide />} />
        <Route path="/q/:token" element={<QuoteAccept />} />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }
  // owner_scoped users land in the scoped portal
  const home = role === 'owner_scoped' ? '/portal' : '/work-orders';
  return (
    <Routes>
      <Route path="/portal" element={<OwnerPortal />} />
      <Route path="/overview" element={<Overview />} />
      <Route path="/reports" element={<Reports />} />
      <Route path="/work-orders" element={<WorkOrders />} />
      <Route path="/due-dates" element={<DueDates />} />
      <Route path="/checklists" element={<ChecklistTemplates />} />
      <Route path="/preventive" element={<Preventive />} />
      <Route path="/assets" element={<AssetsList />} />
      <Route path="/assets/import" element={<CsvImport />} />
      <Route path="/assets/labels" element={<QrLabels />} />
      <Route path="/assets/:id" element={<AssetPage />} />
      <Route path="/properties" element={<PropertiesList />} />
      <Route path="/properties/:id" element={<PropertyMap />} />
      <Route path="/clients" element={<ClientsList />} />
      <Route path="/leads" element={<Leads />} />
      <Route path="/referrers" element={<Referrers />} />
      <Route path="/marketing" element={<Marketing />} />
      <Route path="/catalogue" element={<Catalogue />} />
      <Route path="/quotes" element={<Quotes />} />
      <Route path="/contracts" element={<Contracts />} />
      <Route path="/invoices" element={<Invoices />} />
      <Route path="/sales-dashboard" element={<SalesDashboard />} />
      <Route path="/accounting" element={<Accounting />} />
      <Route path="/suppliers" element={<Suppliers />} />
      <Route path="/management-companies" element={<ManagementCompanies />} />
      <Route path="/directory" element={<Directory />} />
      <Route path="/payables" element={<Payables />} />
      <Route path="/treasury" element={<Treasury />} />
      <Route path="/pnl" element={<Pnl />} />
      <Route path="/statements" element={<Statements />} />
      <Route path="/statements/:id/print" element={<StatementPrint />} />
      <Route path="/profitability" element={<Profitability />} />
      <Route path="/exports" element={<Exports />} />
      <Route path="/finance-dashboard" element={<FinanceDashboard />} />
      <Route path="/invoices/:id/print" element={<InvoicePrint />} />
      <Route path="/quotes/:id/print" element={<QuotePrint />} />
      <Route path="/q/:token" element={<QuoteAccept />} />
      <Route path="/parts" element={<Parts />} />
      <Route path="/tools" element={<Tools />} />
      <Route path="/commercial-settings" element={<CommercialSettings />} />
      <Route path="/users" element={<Users />} />
      <Route path="/settings" element={<Settings />} />
      <Route path="/help" element={<Help />} />
      <Route path="/styleguide" element={<Styleguide />} />
      <Route path="*" element={<Navigate to={home} replace />} />
    </Routes>
  );
}
